import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { ALL_LEAGUE_IDS, leagueById } from "../shared/leagues";
import type { DayPicks, PicksResponse, ProbabilityPick } from "../shared/types";
import { Backtest } from "./pages/Backtest";
import { DoubleRow } from "./components/DoubleRow";
import { HowToRead } from "./components/HowToRead";
import { PickRow } from "./components/PickRow";
import { TopPicks } from "./components/TopPicks";
import { fetchPicks, type FetchPicksOptions } from "./lib/api";
import { formatDayLabel, formatPercent } from "./lib/format";

type View = "singles" | "doubles" | "backtest";
type DoublesMetric = "safe" | "value";
type SinglesRank = "probability" | "ev";

const DEFAULT_MAX_MARGIN = 0.08;
const DEFAULT_MIN_PROB = 0.45;

export function App() {
  const [data, setData] = useState<PicksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>("singles");
  const [doublesMetric, setDoublesMetric] = useState<DoublesMetric>("safe");
  const [singlesRank, setSinglesRank] = useState<SinglesRank>("probability");

  const [leagueIds, setLeagueIds] = useState<ReadonlyArray<string>>(ALL_LEAGUE_IDS);
  const [maxMargin, setMaxMargin] = useState(DEFAULT_MAX_MARGIN);
  const [minProb, setMinProb] = useState(DEFAULT_MIN_PROB);

  const filterOptions = useMemo<FetchPicksOptions>(
    () => ({
      leagues: leagueIds.length === ALL_LEAGUE_IDS.length ? undefined : leagueIds.join(","),
      maxMargin,
      minFairProbability: minProb,
    }),
    [leagueIds, maxMargin, minProb],
  );

  const load = useCallback(async (options: FetchPicksOptions) => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchPicks(options));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unexpected error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(filterOptions);
  }, [filterOptions, load]);

  const isInitialLoad = loading && !data;
  const consensusOn = data?.meta?.consensusEnabled === true;
  const daysWithPicks = useMemo<DayPicks[]>(() => {
    if (!data) return [];
    return data.days.map((d) => ({ ...d, picks: sortedSingles(d.picks, singlesRank, consensusOn) }));
  }, [data, singlesRank, consensusOn]);
  const totalPicksAcrossDays = daysWithPicks.reduce((sum, d) => sum + d.picks.length, 0);

  return (
    <main className="mx-auto box-border flex w-full max-w-[1100px] flex-col gap-3 px-3 py-3 lg:px-5 lg:py-4">
      <header className="panel flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-base font-semibold text-ink-900">Football value tracker</h1>
          <p className="break-words text-xs text-ink-500">
            Picks from Norsk Tipping, with the bookmaker&apos;s margin stripped out
            {data ? ` · refreshed ${formatTime(data.fetchedAt)}` : ""}
            {consensusOn ? " · sharp-book consensus on" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs
            options={[
              { value: "singles", label: "Singles" },
              { value: "doubles", label: "Doubles" },
              { value: "backtest", label: "Backtest" },
            ]}
            value={view}
            onChange={setView}
          />
          {view !== "backtest" ? (
            <button className="btn" disabled={loading} onClick={() => void load(filterOptions)} type="button">
              {loading ? "…" : "Refresh"}
            </button>
          ) : null}
        </div>
      </header>

      {view !== "backtest" ? (
        <>
          <HowToRead />
          <FilterStrip
            leagueIds={leagueIds}
            onLeagueIdsChange={setLeagueIds}
            maxMargin={maxMargin}
            onMaxMarginChange={setMaxMargin}
            minProb={minProb}
            onMinProbChange={setMinProb}
          />
        </>
      ) : null}

      {error ? (
        <div className="panel flex items-center justify-between gap-3 border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <span>{error}</span>
          <button className="btn" onClick={() => void load(filterOptions)} type="button">
            Retry
          </button>
        </div>
      ) : null}

      {view === "singles" ? (
        <>
          {isInitialLoad ? (
            <RowList title="Loading" items={[]} loading empty="" render={() => null} />
          ) : (
            <TopPicks picks={daysWithPicks.flatMap((d) => d.picks)} />
          )}

          {consensusOn ? (
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
              <Tabs
                options={[
                  { value: "probability", label: "By probability" },
                  { value: "ev", label: "By value (+EV)" },
                ]}
                value={singlesRank}
                onChange={setSinglesRank}
              />
              <p className="text-[11px] text-ink-500">
                Day-by-day breakdown below.{" "}
                <span className="text-ink-400">
                  {singlesRank === "probability" ? "Sorted by most likely to win." : "Sorted by best EV."}
                </span>
              </p>
            </div>
          ) : null}

          {!isInitialLoad && daysWithPicks.length === 0 ? null : null}
          {!isInitialLoad
            ? daysWithPicks.map((day) => (
                <RowList
                  key={day.date}
                  title={`${formatDayLabel(day.daysAhead, day.date)} · ${day.picks.length} pick${day.picks.length === 1 ? "" : "s"}`}
                  items={day.picks}
                  loading={false}
                  empty={day.daysAhead < 2 ? "No qualifying picks." : ""}
                  hideWhenEmpty={day.daysAhead >= 2 && day.picks.length === 0}
                  render={(pick, i) => <PickRow key={pick.id} pick={pick} rank={i + 1} />}
                />
              ))
            : null}
          {!isInitialLoad && totalPicksAcrossDays === 0 ? null : null}
        </>
      ) : view === "doubles" ? (
        <>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
            <Tabs
              options={[
                { value: "safe", label: "Safe" },
                { value: "value", label: "Value" },
              ]}
              value={doublesMetric}
              onChange={setDoublesMetric}
            />
            <p className="text-[11px] text-ink-500">
              {doublesMetric === "safe"
                ? "Highest chance both legs win. Lower payout, higher hit rate."
                : "Best expected return per €1. Bigger payouts, lower hit rate."}
            </p>
          </div>
          <RowList
            title={doublesMetric === "safe" ? "Safe doubles" : "Value doubles"}
            items={doublesMetric === "safe" ? (data?.safeDoubles ?? []) : (data?.valueDoubles ?? [])}
            loading={isInitialLoad}
            empty="No doubles available with current filters."
            render={(dbl, i) => <DoubleRow key={dbl.id} double={dbl} rank={i + 1} />}
          />
        </>
      ) : (
        <Backtest />
      )}

      <footer className="text-center text-xs text-ink-500">
        Norsk-only probability proxy. The app does not place bets.
      </footer>
    </main>
  );
}

function sortedSingles(picks: ProbabilityPick[], rank: SinglesRank, consensusOn: boolean): ProbabilityPick[] {
  if (rank === "ev" && consensusOn) {
    return [...picks].sort((a, b) => {
      const aEv = a.evScore ?? -Infinity;
      const bEv = b.evScore ?? -Infinity;
      return bEv - aEv || b.fairProbability - a.fairProbability;
    });
  }
  return picks;
}

function FilterStrip({
  leagueIds,
  onLeagueIdsChange,
  maxMargin,
  onMaxMarginChange,
  minProb,
  onMinProbChange,
}: {
  leagueIds: ReadonlyArray<string>;
  onLeagueIdsChange: (next: ReadonlyArray<string>) => void;
  maxMargin: number;
  onMaxMarginChange: (next: number) => void;
  minProb: number;
  onMinProbChange: (next: number) => void;
}) {
  const allSelected = leagueIds.length === ALL_LEAGUE_IDS.length;

  const toggleLeague = (id: string) => {
    if (leagueIds.includes(id)) {
      onLeagueIdsChange(leagueIds.filter((l) => l !== id));
    } else {
      onLeagueIdsChange([...leagueIds, id]);
    }
  };

  return (
    <details className="panel group p-3 text-sm">
      <summary className="flex cursor-pointer select-none items-center justify-between gap-2 text-ink-700">
        <span className="font-semibold">Filters</span>
        <span className="text-[11px] text-ink-500">
          {allSelected ? "All leagues" : `${leagueIds.length} of ${ALL_LEAGUE_IDS.length} leagues`} · vig ≤{" "}
          {formatPercent(maxMargin)} · min win chance {formatPercent(minProb)}
        </span>
      </summary>

      <div className="mt-3 flex flex-col gap-3">
        <div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">Leagues</p>
            <div className="flex gap-1">
              <button className="btn-link" type="button" onClick={() => onLeagueIdsChange(ALL_LEAGUE_IDS)}>
                Select all
              </button>
              <button className="btn-link" type="button" onClick={() => onLeagueIdsChange([])}>
                None
              </button>
            </div>
          </div>
          <p className="text-[11px] text-ink-500">
            Click a league to toggle it. Only matches in checked leagues will show up.
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {ALL_LEAGUE_IDS.map((id) => {
              const active = leagueIds.includes(id);
              const label = leagueById(id)?.label ?? id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggleLeague(id)}
                  className={`rounded px-2 py-0.5 text-[11px] font-semibold transition ${
                    active
                      ? "bg-pitch-700 text-white"
                      : "border border-ink-100 bg-white text-ink-700 hover:border-ink-500"
                  }`}
                  title={label}
                  aria-pressed={active}
                >
                  {id}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <SliderField
            label="Max bookmaker margin"
            hint="Hide picks where Norsk Tipping's mark-up is above this. Lower = stricter; only the sharpest-priced markets get through."
            value={maxMargin}
            min={0.04}
            max={0.15}
            step={0.005}
            format={formatPercent}
            onChange={onMaxMarginChange}
          />
          <SliderField
            label="Min win chance"
            hint="Hide picks below this estimated win probability. Raise it to only see strong favorites; lower it for closer matches."
            value={minProb}
            min={0.35}
            max={0.75}
            step={0.01}
            format={formatPercent}
            onChange={onMinProbChange}
          />
        </div>
      </div>
    </details>
  );
}

function SliderField({
  label,
  hint,
  value,
  min,
  max,
  step,
  format,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (value: number) => string;
  onChange: (next: number) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-ink-500">
        {label} <span className="text-ink-900 normal-case tracking-normal">{format(value)}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="accent-pitch-700"
      />
      {hint ? <span className="text-[11px] leading-snug text-ink-500">{hint}</span> : null}
    </label>
  );
}

function RowList<T>({
  title,
  items,
  loading,
  empty,
  render,
  hideWhenEmpty = false,
}: {
  title: string;
  items: T[];
  loading: boolean;
  empty: string;
  render: (item: T, index: number) => ReactNode;
  hideWhenEmpty?: boolean;
}) {
  if (!loading && items.length === 0 && hideWhenEmpty) return null;
  return (
    <section className="space-y-1.5">
      <h2 className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">{title}</h2>
      {loading ? (
        <div className="space-y-1.5">
          {Array.from({ length: 6 }, (_, i) => (
            <div className="panel h-10 animate-pulse bg-ink-50" key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="panel p-4 text-center text-sm text-ink-500">{empty}</div>
      ) : (
        <div className="space-y-1.5">{items.map(render)}</div>
      )}
    </section>
  );
}

function Tabs<V extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: V; label: string }[];
  value: V;
  onChange: (next: V) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-ink-100 bg-white p-0.5">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            className={`h-7 rounded px-3 text-xs font-medium transition ${
              active ? "bg-pitch-700 text-white" : "text-ink-700 hover:text-ink-900"
            }`}
            onClick={() => onChange(option.value)}
            type="button"
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function formatTime(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Oslo",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
}
